import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';

/**
 * SPA bootstrap.
 *
 * The deploy wrapper (`.kiro/scripts/deploy-public-website.sh`)
 * materializes `dist/config.json` from the live stack CFN outputs at
 * deploy time. The SPA fetches that file BEFORE mounting React and
 * writes it to `globalThis.__DVGUP_CONFIG__` so every API client + auth
 * hook that reads through `getPublicWebsiteConfig()` /
 * `getCognitoConfig()` picks up the live values on the first render.
 *
 * **API origin policy.** The SPA does NOT call the API Gateway
 * directly — every public REST call goes through the CloudFront
 * `/api/*` cache behavior so the api-passthrough cache and per-`Profile*`
 * bypass behavior apply uniformly AND so the browser does not need
 * cross-origin headers from API Gateway. `publicApiInvokeUrl` is
 * therefore pinned to `/api` regardless of what `config.json` carries.
 * The `apiInvokeUrl` field in `config.json` is consumed server-side by
 * the deploy wrapper's prerender + sitemap steps.
 *
 * **Cognito coordinates.** The bootstrap propagates the `cognito`
 * slice verbatim so the sign-in / sign-up / verify-email pages can
 * read `userPoolId`, `userPoolClientId`, and `hostedUiUrl` at first
 * render. Round-22 Phase D shipped the sign-in UX assuming this slice
 * was available; the bootstrap only wrote `publicApiKey` before, so
 * every auth surface rendered its "temporarily unavailable" banner
 * even when the deploy wrapper had put real values in `/config.json`.
 *
 * Failure-tolerant: if `/config.json` is missing or unparseable, the
 * SPA still mounts and every consumer of `__DVGUP_CONFIG__` falls
 * through to the empty-string sentinel.
 */
interface PublicWebsiteRuntimeConfigPayload {
  readonly apiKey?: string;
  readonly apiInvokeUrl?: string;
  readonly adminApiInvokeUrl?: string;
  readonly cognito?: {
    readonly userPoolId?: string;
    readonly userPoolClientId?: string;
    readonly hostedUiUrl?: string;
  };
}

interface RuntimeConfigGlobal {
  publicApiInvokeUrl: string;
  publicApiKey: string;
  /**
   * Direct admin gateway invoke URL — SPA calls admin endpoints
   * cross-origin with `Authorization: Bearer <id-token>` (CORS
   * allow-list is set on the admin gateway). Empty string until the
   * `AdminApiInvokeUrl` CFN Output populates.
   */
  adminApiInvokeUrl: string;
  cognito: {
    userPoolId: string;
    userPoolClientId: string;
    hostedUiUrl: string;
  };
}

function assignGlobal(value: RuntimeConfigGlobal): void {
  (globalThis as unknown as { __DVGUP_CONFIG__?: RuntimeConfigGlobal }).__DVGUP_CONFIG__ =
    value;
}

function fromPayload(parsed: PublicWebsiteRuntimeConfigPayload, apiKey: string): RuntimeConfigGlobal {
  return {
    publicApiInvokeUrl: '/api',
    publicApiKey: apiKey,
    adminApiInvokeUrl: parsed.adminApiInvokeUrl ?? '',
    cognito: {
      userPoolId: parsed.cognito?.userPoolId ?? '',
      userPoolClientId: parsed.cognito?.userPoolClientId ?? '',
      hostedUiUrl: parsed.cognito?.hostedUiUrl ?? '',
    },
  };
}

async function loadPublicWebsiteConfig(): Promise<void> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (response.ok) {
      const parsed = (await response.json()) as PublicWebsiteRuntimeConfigPayload;
      if (typeof parsed.apiKey === 'string') {
        assignGlobal(fromPayload(parsed, parsed.apiKey));
        return;
      }
    }
  } catch {
    // Fall through to the dev-key fallback below.
  }
  const devKey = import.meta.env.VITE_PUBLIC_API_KEY;
  if (typeof devKey === 'string' && devKey.length > 0) {
    assignGlobal({
      publicApiInvokeUrl: '/api',
      publicApiKey: devKey,
      adminApiInvokeUrl: '',
      cognito: { userPoolId: '', userPoolClientId: '', hostedUiUrl: '' },
    });
  }
}

async function bootstrap(): Promise<void> {
  await loadPublicWebsiteConfig();
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element #root not found in index.html');
  }
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

void bootstrap();
